import React, { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin, Star } from "lucide-react";
import { KineticText, Magnetic, EASE } from "../../lib/motion";

const VIDEO = `${process.env.PUBLIC_URL || ""}/tjh-hero.mp4`;
const POSTER = `${process.env.PUBLIC_URL || ""}/tjh-hero-poster.jpg`;

export default function HeroDawn() {
  const navigate = useNavigate();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.06]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const fade = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-[100svh] flex items-center justify-center px-3 sm:px-6 pt-24 pb-10">
      {/* Floating cinematic video screen */}
      <motion.div
        initial={{ opacity: 0, y: 40, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 1.2, ease: EASE, delay: 0.3 }}
        className="relative w-full max-w-[1360px] mx-auto rounded-[24px] md:rounded-[40px] overflow-hidden shadow-[0_40px_100px_rgba(4,20,14,0.45)] h-[80svh] min-h-[520px]"
      >
        <motion.video
          style={{ scale }}
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster={POSTER}
        >
          <source src={VIDEO} type="video/mp4" />
        </motion.video>

        {/* Legibility overlays */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#04140E]/90 via-[#04140E]/25 to-[#04140E]/45" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_40%,rgba(4,20,14,0.5)_100%)]" />

        {/* Content */}
        <motion.div style={{ y: contentY, opacity: fade }} className="absolute inset-0 flex flex-col justify-end p-6 sm:p-10 md:p-14">
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.9 }}
            className="flex flex-wrap items-center gap-3 mb-5"
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 backdrop-blur-md px-4 py-1.5">
              <MapPin size={13} className="text-[var(--amber-light)]" />
              <span className="font-body text-[11px] sm:text-xs tracking-[0.18em] uppercase text-white/90">The Jackson House &middot; McAllen, TX</span>
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/25 bg-white/10 backdrop-blur-md px-3 py-1.5">
              <Star size={12} className="fill-[var(--amber-light)] text-[var(--amber-light)]" />
              <span className="font-body text-[11px] sm:text-xs font-semibold text-white/90">4.98</span>
            </span>
          </motion.div>

          <h1 className="font-display font-extrabold text-white leading-[0.86] text-[13vw] sm:text-7xl md:text-8xl lg:text-[7.5rem]">
            <KineticText text="A Day" startOnMount delay={0.7} />
            <span className="block">
              <span className="font-serif-i italic font-normal text-[var(--amber-light)]"><KineticText text="in Paradise" startOnMount delay={0.95} /></span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5, duration: 1 }}
            className="mt-5 max-w-xl text-white/80 text-sm sm:text-base font-light leading-relaxed"
          >
            Follow the sun through a 3BD/2BA oasis &mdash; private pool, spa, poker nights and
            starlit soaks. Scroll to live a full day at your RGV escape.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.7, duration: 0.9 }}
            className="flex flex-wrap items-center gap-4 mt-8"
          >
            <Magnetic strength={0.5}>
              <button onClick={() => navigate("/booking")} className="btn-shine sunset-fill text-white font-body font-bold text-base sm:text-lg px-8 py-3.5 rounded-full shadow-xl">
                Reserve Your Stay
              </button>
            </Magnetic>
            <div className="flex items-center gap-2 text-white/70">
              <ArrowDown size={18} className="animate-bounce" />
              <span className="font-body text-sm">Begin the journey</span>
            </div>
          </motion.div>
        </motion.div>

        {/* rotating seal */}
        <motion.div
          animate={{ rotate: 360 }} transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
          className="absolute top-5 right-5 w-20 h-20 md:w-24 md:h-24 hidden sm:block"
        >
          <svg viewBox="0 0 100 100" className="w-full h-full">
            <defs><path id="c" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" /></defs>
            <text className="fill-white/80" fontSize="9.5" letterSpacing="3" fontFamily="Space Grotesk">
              <textPath href="#c">SUNRISE &middot; SUNSET &middot; PARADISE &middot; </textPath>
            </text>
          </svg>
        </motion.div>
      </motion.div>
    </section>
  );
}
